import {
  CookieKey,
  EMAIL_EXIST_OTHER_AUTH_METHOD_ISSUES,
  EmailLoginError,
  EmailStatusCode,
  ErrorMessages,
  MessageField,
  SignUpSource,
} from '@/constants';
import { sendVerificationEmail } from '@/fetchers';
import { updateTrackedShipments } from '@/fetchers/umsAPI/tracking.fetchers';
import {
  type AuthenticateEMSPayload,
  type AuthenticateEmailPayload,
  type AuthenticatePMPayload,
  authenticateEMSFetcher,
  authenticateEmailFetcher,
  authenticatePMFetcher,
  guestLoginFetcher,
  logout as logoutFetcher,
} from '@/fetchers/umsAPI/umsAuthenticator.fetchers';
import type { IAuthenticateSuccessfulResponse, LanguageCode } from '@/interfaces';
import type { PmtOnboardingPopupCta } from '@/interfaces/tracking';
import { userInfoModify } from '@/utils';
import type { OriginalUserInfo } from '@/utils/umsDataModify.utils';
import { useStatsigUser } from '@statsig/react-bindings';
import Cookies from 'js-cookie';
import { useCallback } from 'react';
import useGaEventBaseParams from './useGaEventBaseParams.hook';
import useGeo from './useGeo.hooks';
import useGlobalState from './useGlobalState.hooks';

const TIO_UUID_KEY = 'uuid';
export const REFRESH_TOKEN_EXPIRE_DAYS = 365 * 500; // Set refresh token to expire in 500 years to match with BE
export const DEFAULT_EXPIRE_DAYS = 365; // 1 year

/**
 * Hook that provides authentication related functions
 */
export const useAuth = () => {
  const { isLoggedIn, setIsLoggedIn, setUserInfo, setFirestoreUuid, pmtCurrentLanguageCode, clearUserInfo } =
    useGlobalState();
  const { geoInfo } = useGeo();
  const getGaEventBaseParams = useGaEventBaseParams();
  const { updateUserSync } = useStatsigUser();

  /**
   * Merges tracked shipments from guest accounts into the logged in user's account
   * - Gets guest UUIDs from cookies and localStorage
   * - Updates tracked shipments in UMS with the guest UUIDs
   * - Removes temporary TIO UUID from localStorage on success
   */
  const mergeShipments = async () => {
    const uuids: string[] = [];
    const guestUuid = Cookies.get(CookieKey.UserId);
    const tioGuestUuid = localStorage.getItem(TIO_UUID_KEY);

    if (guestUuid) uuids.push(guestUuid);
    if (tioGuestUuid) uuids.push(tioGuestUuid);

    if (!uuids.length) return;

    const responseStatus = await updateTrackedShipments({
      language: pmtCurrentLanguageCode,
      uuids,
    });

    if (responseStatus === 200) {
      localStorage.removeItem(TIO_UUID_KEY);
    }
  };

  /**
   * This function is used to set necessary cookies and global state to treat user as logged in
   * - Sets access token and refresh token in cookies
   * - Sets user info in global state
   * - Sets firestore uuid in global state
   * - Merge current tracked shipments as a guest to the user's account
   */
  const PMLogin = (userInfo: OriginalUserInfo) => {
    if (!userInfo) return;

    setIsLoggedIn(true);

    const modifiedUserInfo = userInfoModify(userInfo);
    setUserInfo(modifiedUserInfo);
    setFirestoreUuid(modifiedUserInfo.id);
    // Update Statsig user info
    updateUserSync({
      userID: modifiedUserInfo.id,
      custom: {
        email_sync_opt_in: modifiedUserInfo.emailSyncOptIn,
        newsletter_opt_in: modifiedUserInfo.newsletterOptIn,
        subscribe_to_email_summary: modifiedUserInfo.subscribeToEmailSummary,
        opt_in_date: modifiedUserInfo.optInDate ?? undefined,
      },
    });
  };

  // [Login by Google/LinkedIn] Authenticate with email sync
  const authEMS = async ({
    code,
    signUpSource = SignUpSource.ConsumerWeb,
    newsletterOptIn = false,
    scope,
    country,
    userId,
    redirectUri,
    gaClientId,
    gaSessionId,
    eventTimestamp,
    cta,
    authMethod,
    utmParams,
  }: AuthenticateEMSPayload) => {
    try {
      const response = await authenticateEMSFetcher({
        code,
        scope,
        signUpSource,
        newsletterOptIn,
        country,
        userId,
        redirectUri,
        gaClientId,
        gaSessionId,
        eventTimestamp,
        cta,
        authMethod,
        utmParams,
      });

      if (!response.access_token || !response.user_info) {
        throw new Error(ErrorMessages[MessageField.EMAIL_SYNC_CANNOT_AUTHORIZED].error);
      }

      if (!isLoggedIn) {
        // set access token + refresh token --> merge shipment --> set user id
        Cookies.set(CookieKey.AccessToken, response.access_token, { expires: DEFAULT_EXPIRE_DAYS });
        Cookies.set(CookieKey.RefreshToken, response.refresh_token, { expires: REFRESH_TOKEN_EXPIRE_DAYS });

        // This is to merge current tracked shipments as a guest to the user's account
        // Shipments should be merged before setting user id in cookie
        mergeShipments();

        Cookies.set(CookieKey.UserId, response.user_info.id, { expires: DEFAULT_EXPIRE_DAYS });

        PMLogin(response.user_info);
      }

      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  // [Login by Google/LinkedIn] Authenticate without email sync
  const authPM = async ({
    mediumOrigin,
    code,
    signUpSource = SignUpSource.ConsumerWeb,
    newsletterOptIn = false,
    country,
    gaClientId,
    gaSessionId,
    eventTimestamp,
    cta,
    authMethod,
  }: AuthenticatePMPayload) => {
    try {
      const response = await authenticatePMFetcher({
        mediumOrigin,
        code,
        country,
        signUpSource,
        newsletterOptIn,
        gaClientId,
        gaSessionId,
        eventTimestamp,
        cta,
        authMethod,
      });

      const hasError = 'error' in response;

      if (hasError) {
        throw new Error(ErrorMessages[MessageField.AUTHENTICATE_ERROR].error);
      }

      // set access token + refresh token --> merge shipment --> set user id
      Cookies.set(CookieKey.AccessToken, response.access_token, { expires: DEFAULT_EXPIRE_DAYS });
      Cookies.set(CookieKey.RefreshToken, response.refresh_token, { expires: REFRESH_TOKEN_EXPIRE_DAYS });

      // This is to merge current tracked shipments as a guest to the user's account
      // Shipments should be merged before setting user id in cookie
      mergeShipments();

      Cookies.set(CookieKey.UserId, response.user_info.id, { expires: DEFAULT_EXPIRE_DAYS });

      PMLogin(response.user_info);

      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  // [Login by Email] Authenticate with email
  const authEmail = async ({
    email,
    browserUuid,
    signUpSource = SignUpSource.ConsumerWeb,
    newsletterOptIn = false,
    country,
    gaClientId,
    gaSessionId,
    eventTimestamp,
    cta,
    authMethod,
    language,
    variant,
    popupVariant,
  }: AuthenticateEmailPayload & {
    language?: LanguageCode;
    variant?: string;
    popupVariant?: string;
  }) => {
    try {
      const response = await authenticateEmailFetcher({
        email,
        browserUuid,
        country,
        signUpSource,
        newsletterOptIn,
        gaClientId,
        gaSessionId,
        eventTimestamp,
        cta,
        authMethod,
      });

      const hasError = 'error' in response;
      if (hasError) return;

      const authResponse = response as IAuthenticateSuccessfulResponse;

      switch (authResponse.status) {
        case EmailStatusCode.Verified:
          Cookies.set(CookieKey.AccessToken, authResponse.access_token, { expires: DEFAULT_EXPIRE_DAYS });
          Cookies.set(CookieKey.RefreshToken, authResponse.refresh_token, { expires: REFRESH_TOKEN_EXPIRE_DAYS });
          Cookies.set(CookieKey.UserId, authResponse.user_info.id, { expires: DEFAULT_EXPIRE_DAYS });

          PMLogin(authResponse.user_info);
          break;

        case EmailStatusCode.Unverified: {
          // set access token + refresh token --> merge shipment --> set user id
          Cookies.set(CookieKey.AccessToken, authResponse.access_token, { expires: DEFAULT_EXPIRE_DAYS });
          Cookies.set(CookieKey.RefreshToken, authResponse.refresh_token, { expires: REFRESH_TOKEN_EXPIRE_DAYS });

          // Shipments should be merged before setting user id in cookie
          mergeShipments();

          Cookies.set(CookieKey.UserId, authResponse.user_info.id, { expires: DEFAULT_EXPIRE_DAYS });
          Cookies.set(CookieKey.BrowserUuid, authResponse.browser_uuid, { expires: DEFAULT_EXPIRE_DAYS });

          setUserInfo(userInfoModify(authResponse.user_info));
          setFirestoreUuid(authResponse.user_info.id);

          if (language) {
            await sendVerificationEmail({
              email,
              browserUuid: authResponse.browser_uuid,
              language,
              source: signUpSource,
              cta: cta as PmtOnboardingPopupCta,
              variant,
              popupVariant,
            });
          }
          break;
        }

        case EmailStatusCode.ExistOtherIssues:
          throw {
            type: EMAIL_EXIST_OTHER_AUTH_METHOD_ISSUES,
            authMethod: EmailLoginError[authResponse.error_code],
          };
      }

      return authResponse;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  /**
   * Handles user logout
   * - Clears all authentication related cookies
   * - Creates a new guest UUID
   * - Updates global state
   */
  const logout = useCallback(async () => {
    try {
      const refreshToken = Cookies.get(CookieKey.RefreshToken);
      if (!refreshToken) return false;

      const gaEventBaseParams = getGaEventBaseParams();
      const res = await logoutFetcher({
        refreshToken,
        ...gaEventBaseParams,
      });
      if (res?.status_code !== 200) return false;

      clearUserInfo();
      // Update Statsig user info
      updateUserSync({ userID: undefined });

      return true;
    } catch (error) {
      console.error('Logout error:', error);
      return false;
    }
  }, [clearUserInfo, updateUserSync]);

  const authGuest = async () => {
    const data = await guestLoginFetcher({ country: geoInfo.country });

    Cookies.set(CookieKey.AccessToken, data.access_token, { expires: DEFAULT_EXPIRE_DAYS });
    Cookies.set(CookieKey.RefreshToken, data.refresh_token, { expires: REFRESH_TOKEN_EXPIRE_DAYS });
    Cookies.set(CookieKey.UserId, data.user_info.id, { expires: DEFAULT_EXPIRE_DAYS });

    setFirestoreUuid(data.user_info.id);

    return data;
  };

  return {
    authEMS,
    authPM,
    authEmail,
    logout,
    authGuest,
    PMLogin,
  };
};

export default useAuth;
